import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  BarChart3, 
  Search, 
  FileText, 
  TrendingUp, 
  Star, 
  Globe, 
  Share2, 
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
  MessageSquare,
  Bell,
  Sparkles,
  Users,
  Zap
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import TrustVerifier from "@/components/TrustVerifier";
import SelleTrustAssurance from "@/components/SelleTrustAssurance";
import ComplianceAnalyzer from "@/components/ComplianceAnalyzer";

const features = [
  {
    icon: Search,
    title: "Business Analysis",
    description: "Instant snapshot reports analyzing online presence across 8 key dimensions with AI-powered insights."
  },
  {
    icon: Star,
    title: "Review Monitoring",
    description: "Track reviews from Google, Yelp, Facebook, BBB, and 50+ platforms in one unified dashboard."
  },
  {
    icon: MessageSquare,
    title: "AI Response Engine",
    description: "Generate professional, personalized responses to reviews in seconds with our intelligent AI."
  },
  {
    icon: Bell,
    title: "Real-Time Alerts",
    description: "Get instant notifications when new reviews are posted. Never miss customer feedback again."
  },
  {
    icon: ShieldCheck,
    title: "Reputation Protection",
    description: "Identify negative content early and respond strategically to protect your brand image."
  },
  {
    icon: TrendingUp,
    title: "Growth Insights",
    description: "Track sentiment trends, response rates, and reputation scores with actionable recommendations."
  }
];

const platforms = [
  "Google", "Yelp", "Facebook", "BBB", "Trustpilot", "TripAdvisor",
  "Healthgrades", "G2", "Capterra", "Apple Maps", "Angi", "Zillow"
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-24 px-4 bg-gradient-to-b from-blue-50 to-white" data-testid="hero-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="bg-blue-100 text-blue-700 px-4 py-2 mb-8 animate-fade-in-up">
              <Sparkles className="h-4 w-4 mr-2 inline" />
              Trusted by 500+ Businesses
            </Badge>
            
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight mb-6 animate-fade-in-up animate-delay-100">
              Build Trust Through
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-cyan-500 block mt-2">
                Reputation Management
              </span>
            </h1>
            
            <p className="text-lg text-slate-600 mb-10 max-w-2xl mx-auto animate-fade-in-up animate-delay-200">
              Analyze your online presence, monitor reviews across 50+ platforms, 
              and respond with AI-powered precision. Everything you need to grow your reputation.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in-up animate-delay-300">
              <Link to="/lookup">
                <Button 
                  size="lg" 
                  className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg hover:shadow-xl transition-all"
                  data-testid="get-started-btn"
                >
                  Analyze Your Business Free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/pricing">
                <Button 
                  variant="outline" 
                  size="lg" 
                  className="border-blue-200 hover:bg-blue-50 hover:text-slate-900 px-8 py-6 text-lg rounded-xl"
                  data-testid="view-pricing-btn"
                >
                  View Pricing
                </Button>
              </Link>
            </div>
          </div>
          
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20 animate-fade-in-up animate-delay-400">
            {[
              { value: "50+", label: "Review Platforms" },
              { value: "AI", label: "Powered Responses" },
              { value: "24/7", label: "Monitoring" },
              { value: "3x", label: "ROI Average" }
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl md:text-4xl font-bold text-blue-600 font-['Outfit']">{stat.value}</div>
                <div className="text-sm text-slate-500 mt-1">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Trust Verifier Section - FREE TOOL */}
      <section className="py-16 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-6xl mx-auto px-4">
          <TrustVerifier />
        </div>
      </section>
      
      {/* SelleTrust Assurance - 7 R's */}
      <SelleTrustAssurance />
      
      {/* Compliance Analyzer - Rate My Problem */}
      <section className="py-16 bg-white" data-testid="compliance-section">
        <div className="max-w-6xl mx-auto px-4">
          <ComplianceAnalyzer />
        </div>
      </section>
      
      {/* How It Works */}
      <section className="py-24 px-4 bg-white" data-testid="how-it-works">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-700 mb-4">How It Works</Badge>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Your Reputation, Managed in Minutes
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              From analysis to action in three simple steps
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                step: "1",
                title: "Analyze",
                description: "Get a comprehensive snapshot report of your business's online presence across all platforms.",
                icon: Search
              },
              {
                step: "2", 
                title: "Monitor",
                description: "Track all reviews in real-time from 50+ platforms. Get instant alerts for new feedback.",
                icon: Bell
              },
              {
                step: "3",
                title: "Respond",
                description: "Use AI to craft perfect responses. Build trust and turn critics into advocates.",
                icon: MessageSquare
              }
            ].map((item, i) => (
              <div key={i} className="relative">
                <div className="bg-blue-50 rounded-2xl p-8 h-full">
                  <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center mb-6 text-white font-bold text-xl">
                    {item.step}
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-3">{item.title}</h3>
                  <p className="text-slate-600">{item.description}</p>
                </div>
                {i < 2 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 transform -translate-y-1/2">
                    <ArrowRight className="h-8 w-8 text-blue-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-24 px-4 bg-slate-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <Badge className="bg-blue-100 text-blue-700 mb-4">Features</Badge>
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Everything You Need to Build Trust
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              Comprehensive tools for reputation analysis, review management, and customer engagement.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, i) => (
              <Card 
                key={i} 
                className="border-slate-200 hover:border-blue-300 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 bg-white"
                data-testid={`feature-card-${i}`}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                    <feature.icon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
      
      {/* Platforms Section */}
      <section className="py-24 px-4 bg-white" data-testid="platforms-section">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">
              Monitor Reviews Everywhere
            </h2>
            <p className="text-slate-600">
              One dashboard for all your reviews across 50+ platforms
            </p>
          </div>
          
          <div className="flex flex-wrap justify-center gap-4">
            {platforms.map((platform) => (
              <Badge 
                key={platform} 
                variant="outline" 
                className="px-5 py-2.5 text-sm bg-white border-slate-200 hover:border-blue-300 transition-colors"
              >
                {platform}
              </Badge>
            ))}
            <Badge className="px-5 py-2.5 text-sm bg-blue-100 text-blue-700">
              +38 more
            </Badge>
          </div>
        </div>
      </section>
      
      {/* Social Proof */}
      <section className="py-24 px-4 bg-blue-600" data-testid="social-proof">
        <div className="max-w-4xl mx-auto text-center text-white">
          <div className="flex justify-center mb-6">
            {[1,2,3,4,5].map((i) => (
              <Star key={i} className="h-8 w-8 fill-yellow-400 text-yellow-400" />
            ))}
          </div>
          <blockquote className="text-2xl font-medium mb-6">
            "Selletrust transformed how we manage our online reputation. We've seen a 40% increase in positive reviews and respond to feedback 5x faster."
          </blockquote>
          <div className="flex items-center justify-center gap-3">
            <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <Users className="h-6 w-6" />
            </div>
            <div className="text-left">
              <div className="font-semibold">Marketing Director</div>
              <div className="text-blue-200 text-sm">Multi-Location Restaurant Group</div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA Section */}
      <section className="py-24 px-4 bg-slate-900" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <Zap className="h-12 w-12 text-yellow-400 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-white mb-6">
            Ready to Take Control of Your Reputation?
          </h2>
          <p className="text-slate-400 mb-10 max-w-2xl mx-auto">
            Start with a free business analysis. See exactly where you stand and get actionable recommendations to improve.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/lookup">
              <Button 
                size="lg" 
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg rounded-xl"
                data-testid="cta-analyze-btn"
              >
                Get Free Analysis
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/pricing">
              <Button 
                size="lg" 
                variant="outline"
                className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg rounded-xl"
              >
                View Plans
              </Button>
            </Link>
          </div>
          
          <div className="flex items-center justify-center gap-8 mt-10 text-slate-400 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              14-day free trial
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              No credit card required
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-400" />
              Cancel anytime
            </div>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
}
